// Cloud auth boundary — the web-only gate between the login screen and the app.
//
// This is a HOST concern, deliberately kept in apps/cloud-web (not @onestone/app):
// the desktop renderer runs on loopback and must NEVER show this cloud login. It
// mirrors how the Router choice is a host concern — the shared app tree stays
// identical across surfaces; only the host decides how you get in.
//
// Session source of truth is Better Auth's useSession() (its own store, no
// TanStack Query needed). While it resolves we show a minimal splash; unauthorized
// → CloudLogin; authorized → the app (children). The login + splash branches are
// wrapped in the shared ThemeProvider so they honor the user's light/dark choice;
// the authorized branch gets its ThemeProvider from AppProviders inside children.
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { ThemeProvider } from '@/components/ThemeProvider';
import { HostAccountContext, type HostAccount } from '@/lib/hostAccount';
import { onUnauthorized } from '@/lib/api';
import { authClient } from './authClient';
import { CloudLogin } from './CloudLogin';

function CloudSplash() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground motion-reduce:animate-none" />
    </div>
  );
}

export function CloudAuthGate({ children }: { children: ReactNode }) {
  const { data, isPending, refetch } = authClient.useSession();

  // A session that ended while the page was open (expired, signed out
  // elsewhere) shows up as the API's 401: re-read it, and this gate shows
  // the sign-in once it's gone — instead of every request failing. One re-read
  // at a time: several requests can fail together.
  const rereading = useRef(false);
  useEffect(
    () =>
      onUnauthorized(() => {
        if (rereading.current) return;
        rereading.current = true;
        void Promise.resolve(refetch()).finally(() => {
          rereading.current = false;
        });
      }),
    [refetch],
  );

  // Host-account seam: gives the shared tree (AccountMenu in TopNav /
  // MobileLayout) the signed-in identity + sign-out. signOut ends the Better
  // Auth session; useSession() observes it and this gate flips back to
  // CloudLogin — no manual redirect needed. lastSyncedAt arrives with the
  // desktop↔cloud sync slice (never fabricated before then).
  const account = useMemo<HostAccount | null>(() => {
    const user = data?.user;
    if (!user) return null;
    return {
      name: user.name ?? '',
      email: user.email,
      image: user.image ?? null,
      signOut: async () => {
        await authClient.signOut();
      },
      // POST /api/auth/delete-user: the worker wipes the user's data, then Better Auth deletes the
      // account (workers/cloud/src/auth.ts). SESSION_EXPIRED = the sign-in is older than its fresh
      // window. On success the page reloads, so the gate re-reads the (now absent) session.
      deleteAccount: async () => {
        const { error } = await authClient.deleteUser();
        if (!error) {
          window.location.assign('/');
          return 'deleted';
        }
        if (error.code === 'SESSION_EXPIRED') return 'reauth';
        throw new Error(error.message || 'Your account could not be deleted. Nothing was removed.');
      },
    };
  }, [data?.user]);

  if (isPending) {
    return (
      <ThemeProvider>
        <CloudSplash />
      </ThemeProvider>
    );
  }

  if (!data?.session) {
    return (
      <ThemeProvider>
        <CloudLogin />
      </ThemeProvider>
    );
  }

  return <HostAccountContext.Provider value={account}>{children}</HostAccountContext.Provider>;
}
